import { useCallback, useEffect, useRef, useState } from 'react';
import { clearComposerDraft, readComposerDraft, writeComposerDraft } from '../utils/composerDraft';

/**
 * The composer's text, persisted per session (see utils/composerDraft.ts), and
 * whether what is on screen was restored rather than typed.
 *
 * A restored draft looks exactly like something the user just wrote. On
 * 「新建对话」 it appeared in an otherwise empty composer with nothing to say
 * where it came from and no way out but selecting and deleting (I-004). The
 * notice flag stays up only while the restored text is still exactly what is
 * on screen: the first edit, a send or a prefill takes it down.
 */
export function useComposerDraft(sessionId: string | null | undefined) {
  const [prompt, setPrompt] = useState(() => readComposerDraft(sessionId));
  const [restoredDraft, setRestoredDraft] = useState(prompt);

  // Swap the buffer when the session changes, and keep the current session's
  // text saved as it is typed.
  const draftSessionRef = useRef(sessionId);
  const promptRef = useRef(prompt);
  promptRef.current = prompt;
  useEffect(() => {
    if (draftSessionRef.current === sessionId) return;
    writeComposerDraft(draftSessionRef.current, promptRef.current);
    draftSessionRef.current = sessionId;
    const restored = readComposerDraft(sessionId);
    setPrompt(restored);
    setRestoredDraft(restored);
  }, [sessionId]);

  useEffect(() => {
    if (draftSessionRef.current !== sessionId) return;
    const timer = setTimeout(() => writeComposerDraft(sessionId, prompt), 400);
    return () => clearTimeout(timer);
  }, [prompt, sessionId]);

  const discardRestoredDraft = useCallback(() => {
    clearComposerDraft(sessionId);
    setPrompt('');
    setRestoredDraft('');
  }, [sessionId]);

  return {
    prompt,
    setPrompt,
    showRestoredDraftNotice: restoredDraft !== '' && prompt === restoredDraft,
    discardRestoredDraft,
  };
}
